import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Plus,
    Edit,
    Trash2,
    CheckCircle,
    X,
    Percent,
    Calendar,
    FileText,
    Layers,
    Shield,
    Info,
    ChevronRight,
    ChevronLeft,
    Check,
    Minus,
    Eye,
    Copy,
    Bell,
    Users,
    Coins,
    Lock
} from 'lucide-react';
import Swal from 'sweetalert2';
import {
    fetchAvanceRequest,
    createAvanceRequest,
    updateAvanceRequest,
    deleteAvanceRequest,
} from '../../../../redux/actions/avanceActions';
import '../parametresStyles/ParamAvances.css';
/* ─── Toggle component ───────────────────────────────────────── */
const Toggle = ({ checked, onChange, label, disabled }) => (
    <button
        type="button"
        className={`avToggle ${checked ? "is-on" : ""}`}
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        aria-pressed={checked}
    >
        <span className="avToggle__thumb" />
        {label && <span className="avToggle__label">{label}</span>}
    </button>
);

/* ─── Tooltip component ──────────────────────────────────────── */
const Tip = ({ text }) => (
    <span className="avTip" data-tip={text}>
        <Info size={13} />
    </span>
);

/* ─── JSON Parser & Packer Helpers ───────────────────────────── */
const ECHEANCE_OPTIONS = [
    { value: 'M_IMMEDIAT', label: 'Même mois (M)' },
    { value: 'M+1', label: 'Mois suivant (M+1)' },
    { value: 'M+2', label: '2 mois plus tard (M+2)' },
    { value: 'M+3', label: '3 mois plus tard (M+3)' },
];

const normalizeEcheances = (value) => {
    if (Array.isArray(value)) {
        const clean = value.filter(Boolean);
        return clean.length > 0 ? clean : ['M+1'];
    }
    if (typeof value === 'string' && value.trim()) {
        return value.split(',').map(v => v.trim()).filter(Boolean);
    }
    return ['M+1'];
};

const getEcheanceLabel = (value) => (
    ECHEANCE_OPTIONS.find(option => option.value === value)?.label || value
);

const formatEcheances = (value) => normalizeEcheances(value).map(getEcheanceLabel).join(', ');

const parseDescription = (descString) => {
    try {
        const obj = JSON.parse(descString);
        if (obj && typeof obj === 'object') {
            return {
                text: obj.text || '',
                code: obj.code || '',
                couleur: obj.couleur || '#204385',
                delaiReponse: obj.delaiReponse ?? 3,
                notifications: obj.notifications ?? true,
                mensualitesMin: obj.mensualitesMin ?? 1,
                mensualitesMax: obj.mensualitesMax ?? '',
                dateDebut: normalizeEcheances(obj.dateDebut),
                echeanceParDefaut: normalizeEcheances(obj.dateDebut).includes(obj.echeanceParDefaut)
                    ? obj.echeanceParDefaut
                    : normalizeEcheances(obj.dateDebut)[0],
                statutsEligibles: obj.statutsEligibles || ['CDI', 'CDD'],
                maxDemandes: obj.maxDemandes ?? 1,
                delaiCarence: obj.delaiCarence ?? '',
                ribObligatoire: obj.ribObligatoire ?? false,
                rubriqueOctroi: obj.rubriqueOctroi || '',
                rubriqueRetenue: obj.rubriqueRetenue || '',
                ordrePriorite: obj.ordrePriorite ?? 1
            };
        }
    } catch (e) {
        // Fallback pour les descriptions texte héritées (legacy data)
    }

    return {
        text: descString || '',
        code: '',
        couleur: '#204385',
        delaiReponse: 3,
        notifications: true,
        mensualitesMin: 1,
        mensualitesMax: '',
        montantMensualiteMinPercent: '',
        montantMensualiteMaxPercent: '',
        dateDebut: ['M+1'],
        echeanceParDefaut: 'M+1',
        statutsEligibles: ['CDI', 'CDD'],
        maxDemandes: 1,
        delaiCarence: '',
        ribObligatoire: false,
        rubriqueOctroi: '',
        rubriqueRetenue: '',
        ordrePriorite: 1
    };
};

const packDescription = (formData) => {
    return JSON.stringify({
        text: formData.text || '',
        code: formData.code || '',
        couleur: formData.couleur || '#204385',
        delaiReponse: formData.delaiReponse !== '' ? Number(formData.delaiReponse) : 3,
        notifications: !!formData.notifications,
        mensualitesMin: formData.nature === 'avance' ? '' : (formData.mensualitesMin !== '' ? Number(formData.mensualitesMin) : 1),
        mensualitesMax: formData.nature === 'avance' ? '' : (formData.mensualitesMax !== '' ? Number(formData.mensualitesMax) : ''),
        dateDebut: normalizeEcheances(formData.dateDebut),
        echeanceParDefaut: normalizeEcheances(formData.dateDebut).includes(formData.echeanceParDefaut)
            ? formData.echeanceParDefaut
            : normalizeEcheances(formData.dateDebut)[0],
        statutsEligibles: formData.statutsEligibles || [],
        maxDemandes: formData.maxDemandes !== '' ? Number(formData.maxDemandes) : 1,
        ribObligatoire: !!formData.ribObligatoire,
        rubriqueOctroi: formData.rubriqueOctroi || '',
        rubriqueRetenue: formData.rubriqueRetenue || '',
        ordrePriorite: formData.ordrePriorite !== '' ? Number(formData.ordrePriorite) : 1
    });
};

const CONTRACT_OPTIONS = ['CDI', 'CDD'];

const ParamAvances = () => {
    const dispatch = useDispatch();
    const { avances, loading, error } = useSelector((state) => state.avance || {
        avances: [],
        loading: false,
        error: null,
    });

    const [showModal, setShowModal] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [viewItem, setViewItem] = useState(null);
    const [stepErrors, setStepErrors] = useState({});

    const [formData, setFormData] = useState({
        nom: '',
        code: '',
        couleur: '#204385',
        actif: true,
        nature: 'avance', // 'avance' | 'pret'
        mensualitesMin: 1,
        mensualitesMax: '',
        montantMensualiteMinPercent: '',
        montantMensualiteMaxPercent: '',
        taux: 0,
        dateDebut: ['M+1'],
        echeanceParDefaut: 'M+1',
        comportementDepart: true, // maps to remboursementAnticipe
        delaiReponse: 3,
        notifications: true,
        anciennete: 0, // maps to seniority Min (months)
        statutsEligibles: ['CDI', 'CDD'],
        maxDemandes: 1,
        delaiCarence: '',
        justificatifRequis: false, // maps to typeJustificatif (FACTURE vs AUCUN)
        justificatifObligatoire: false, // maps to obligatoire
        ribObligatoire: false,
        rubriqueOctroi: '',
        rubriqueRetenue: '',
        ordrePriorite: 1,
        text: ''
    });

    useEffect(() => {
        dispatch(fetchAvanceRequest());
    }, [dispatch]);

    /* ── form helpers ── */
    const set = useCallback((key, val) =>
        setFormData(p => ({ ...p, [key]: val })), []);

    /* ── open / close drawer ── */
    const openCreate = () => {
        setEditItem(null);
        setViewItem(null);
        setFormData({
            nom: '',
            code: '',
            couleur: '#204385',
            actif: true,
            nature: 'avance',
            mensualitesMin: 1,
            mensualitesMax: '',
            montantMensualiteMinPercent: '',
            montantMensualiteMaxPercent: '',
            taux: 0,
            dateDebut: ['M+1'],
            echeanceParDefaut: 'M+1',
            comportementDepart: true,
            delaiReponse: 3,
            notifications: true,
            anciennete: 0,
            statutsEligibles: ['CDI', 'CDD'],
            maxDemandes: 1,
            delaiCarence: '',
            justificatifRequis: false,
            justificatifObligatoire: false,
            ribObligatoire: false,
            rubriqueOctroi: '',
            rubriqueRetenue: '',
            ordrePriorite: 1,
            text: ''
        });
        setStepErrors({});
        setShowModal(true);
    };

    const openEdit = (avance) => {
        setEditItem(avance);
        setViewItem(null);
        const extra = parseDescription(avance.description);
        setFormData({
            nom: avance.type || '',
            code: extra.code || '',
            couleur: extra.couleur || '#204385',
            actif: avance.actif ?? true,
            nature: avance.categorie || 'avance',
            mensualitesMin: extra.mensualitesMin ?? 1,
            mensualitesMax: extra.mensualitesMax ?? (avance.nbMensualiteRemboursement || ''),
            montantMensualiteMinPercent: avance.montantMensualiteMinPercent ?? '',
            montantMensualiteMaxPercent: avance.montantMensualiteMaxPercent ?? '',
            taux: avance.tauxInteret ?? 0,
            dateDebut: normalizeEcheances(extra.dateDebut),
            echeanceParDefaut: normalizeEcheances(extra.dateDebut).includes(extra.echeanceParDefaut)
                ? extra.echeanceParDefaut
                : normalizeEcheances(extra.dateDebut)[0],
            comportementDepart: avance.remboursementAnticipe ?? true,
            delaiReponse: extra.delaiReponse ?? 3,
            notifications: extra.notifications ?? true,
            anciennete: avance.ancienneteMinimale ?? 0,
            statutsEligibles: extra.statutsEligibles || (avance.typeContrat ? avance.typeContrat.split(',') : ['CDI', 'CDD']),
            maxDemandes: extra.maxDemandes ?? 1,
            delaiCarence: extra.delaiCarence ?? '',
            justificatifRequis: avance.typeJustificatif && avance.typeJustificatif !== 'AUCUN',
            justificatifObligatoire: avance.obligatoire ?? false,
            ribObligatoire: extra.ribObligatoire ?? false,
            rubriqueOctroi: extra.rubriqueOctroi || '',
            rubriqueRetenue: extra.rubriqueRetenue || '',
            ordrePriorite: extra.ordrePriorite ?? 1,
            text: extra.text || ''
        });
        setStepErrors({});
        setShowModal(true);
    };

    const openDuplicate = (avance) => {
        setEditItem(null);
        setViewItem(null);
        const extra = parseDescription(avance.description);
        setFormData({
            nom: `Copie de ${avance.type}`,
            code: extra.code ? `${extra.code}CP` : '',
            couleur: extra.couleur || '#204385',
            actif: true,
            nature: avance.categorie || 'avance',
            mensualitesMin: extra.mensualitesMin ?? 1,
            mensualitesMax: extra.mensualitesMax ?? (avance.nbMensualiteRemboursement || ''),
            montantMensualiteMinPercent: avance.montantMensualiteMinPercent ?? '',
            montantMensualiteMaxPercent: avance.montantMensualiteMaxPercent ?? '',
            taux: avance.tauxInteret ?? 0,
            dateDebut: normalizeEcheances(extra.dateDebut),
            echeanceParDefaut: normalizeEcheances(extra.dateDebut).includes(extra.echeanceParDefaut)
                ? extra.echeanceParDefaut
                : normalizeEcheances(extra.dateDebut)[0],
            comportementDepart: avance.remboursementAnticipe ?? true,
            delaiReponse: extra.delaiReponse ?? 3,
            notifications: extra.notifications ?? true,
            anciennete: avance.ancienneteMinimale ?? 0,
            statutsEligibles: extra.statutsEligibles || (avance.typeContrat ? avance.typeContrat.split(',') : ['CDI', 'CDD']),
            maxDemandes: extra.maxDemandes ?? 1,
            delaiCarence: extra.delaiCarence ?? '',
            justificatifRequis: avance.typeJustificatif && avance.typeJustificatif !== 'AUCUN',
            justificatifObligatoire: avance.obligatoire ?? false,
            ribObligatoire: extra.ribObligatoire ?? false,
            rubriqueOctroi: extra.rubriqueOctroi || '',
            rubriqueRetenue: extra.rubriqueRetenue || '',
            ordrePriorite: extra.ordrePriorite ?? 1,
            text: extra.text || ''
        });
        setStepErrors({});
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditItem(null);
        setStepErrors({});
    };

    /* ── toggle active directly from card ── */
    const toggleActif = (avance) => {
        const payload = {
            ...avance,
            actif: !avance.actif,
        };
        dispatch(updateAvanceRequest(avance.id, payload));
    };

    /* ── delete ── */
    const handleDelete = (avance) => {
        Swal.fire({
            title: 'Confirmer la suppression ?',
            text: `Voulez-vous vraiment supprimer définitivement le produit "${avance.type}" ?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Oui, supprimer',
            cancelButtonText: 'Annuler',
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(deleteAvanceRequest(avance.id));
            }
        });
    };

    /* ── duplicate from card ── */
    const handleDuplicate = (avance) => {
        openDuplicate(avance);
    };

    /* ── submit form ── */
    const handleSubmit = (e) => {
        if (e && e.preventDefault) e.preventDefault();

        // Validations
        const errs = {};
        if (!formData.nom?.trim()) errs.nom = "Requis";
        if (!formData.code?.trim()) errs.code = "Requis";
        if (formData.nature === 'pret' && !formData.mensualitesMax) errs.mensualitesMax = "Requis";

        setStepErrors(errs);

        if (Object.keys(errs).length > 0) {
            Swal.fire({
                icon: 'error',
                title: 'Champs obligatoires manquants',
                text: 'Veuillez renseigner tous les champs obligatoires marqués d\'une étoile (*).',
                confirmButtonColor: '#204385',
            });
            return;
        }

        const serializedDesc = packDescription(formData);

        const payload = {
            type: formData.nom,
            description: serializedDesc,
            categorie: formData.nature,
            ancienneteMinimale: formData.anciennete ? Number(formData.anciennete) : 0,
            typeContrat: (formData.statutsEligibles || []).join(','),
            nbMensualiteRemboursement: formData.nature === 'avance' ? 1 : Number(formData.mensualitesMax),
            tauxInteret: formData.taux ? Number(formData.taux) : 0,
            modeRetenue: 'salaire', // par défaut requis par le backend
            autreModeRetenue: '',
            remboursementAnticipe: !!formData.comportementDepart,
            typeJustificatif: formData.justificatifRequis ? 'FACTURE' : 'AUCUN',
            obligatoire: !!formData.justificatifObligatoire,
            actif: !!formData.actif,
            montantMensualiteMinPercent: formData.nature === 'avance' ? null : (formData.montantMensualiteMinPercent !== '' ? Number(formData.montantMensualiteMinPercent) : null),
            montantMensualiteMaxPercent: formData.nature === 'avance' ? null : (formData.montantMensualiteMaxPercent !== '' ? Number(formData.montantMensualiteMaxPercent) : null)
        };

        if (editItem) {
            dispatch(updateAvanceRequest(editItem.id, payload));
        } else {
            dispatch(createAvanceRequest(payload));
        }
        closeModal();
    };

    /* ── toggle contract eligibility selection ── */
    const toggleContract = (contract) => {
        const current = formData.statutsEligibles || [];
        if (current.includes(contract)) {
            set("statutsEligibles", current.filter(c => c !== contract));
        } else {
            set("statutsEligibles", [...current, contract]);
        }
    };

    const toggleEcheance = (echeance) => {
        const current = normalizeEcheances(formData.dateDebut);
        if (current.includes(echeance) && current.length > 1) {
            const updated = current.filter(item => item !== echeance);
            setFormData(prev => ({
                ...prev,
                dateDebut: updated,
                echeanceParDefaut: prev.echeanceParDefaut === echeance ? updated[0] : prev.echeanceParDefaut
            }));
        } else if (!current.includes(echeance)) {
            set("dateDebut", [...current, echeance]);
        }
    };

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
       RENDER DRAWERS & MODALS
    â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
    const renderModalForm = () => {
        if (!showModal) return null;
        const isEdit = !!editItem;

        return (
            <div className="avModal" onClick={e => e.target === e.currentTarget && closeModal()}>
                <div className="avModal__panel">
                    {/* Header */}
                    <div className="avModal__header">
                        <div>
                            <h2 className="avModal__title">
                                {isEdit ? 'Modifier un produit' : 'Nouveau produit de financement'}
                            </h2>
                            <span className="avModal__subtitle">
                                Configurez les caractéristiques d'octroi et de remboursement.
                            </span>
                        </div>
                        <button className="avModal__close" onClick={closeModal} type="button">
                            <X size={16} />
                        </button>
                    </div>

                    {/* Scrollable Body */}
                    <div className="avModal__body">
                        <form onSubmit={e => e.preventDefault()} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            
                            {/* BLOCK 1: Identité */}
                            <div className="avFormBlock">
                                <div className="avFormBlock__title">1. Identité & Caractéristiques</div>
                                <div className="avWizGrid">
                                    <div className="avWizField is-full">
                                        <label>Nom du produit <span className="avReq">*</span></label>
                                        <div className="avColorInput">
                                            <input
                                                type="color"
                                                className="avColorSwatch"
                                                value={formData.couleur}
                                                onChange={e => set("couleur", e.target.value)}
                                                title="Couleur du produit"
                                            />
                                            <input
                                                type="text"
                                                className={`avInput ${stepErrors.nom ? 'is-error' : ''}`}
                                                placeholder="ex: Avance sur salaire exceptionnelle, Prêt immobilier interne..."
                                                value={formData.nom}
                                                onChange={e => set("nom", e.target.value)}
                                            />
                                            {formData.nom && formData.code && (
                                                <span className="avCodePreview" style={{ background: formData.couleur }}>
                                                    {formData.code}
                                                </span>
                                            )}
                                        </div>
                                        {stepErrors.nom && <span className="avErrMsg">{stepErrors.nom}</span>}
                                    </div>

                                    <div className="avWizField">
                                        <label>Libellé <span className="avReq">*</span> <Tip text="Identifiant interne abrégé (ex: AV-SAL, PR-LOG)" /></label>
                                        <input
                                            type="text"
                                            className={`avInput ${stepErrors.code ? 'is-error' : ''}`}
                                            placeholder="AV-SAL"
                                            maxLength={10}
                                            value={formData.code}
                                            onChange={e => set("code", e.target.value.toUpperCase())}
                                        />
                                        {stepErrors.code && <span className="avErrMsg">{stepErrors.code}</span>}
                                    </div>

                                    <div className="avWizField">
                                        <label>Statut actif</label>
                                        <div className="avToggleRow">
                                            <Toggle
                                                checked={!!formData.actif}
                                                onChange={v => set("actif", v)}
                                            />
                                            <span className={`avToggleStatus ${formData.actif ? 'is-on' : ''}`}>
                                                {formData.actif ? 'Actif' : 'Inactif'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="avWizField is-full">
                                        <label>Description (interne RH)</label>
                                        <textarea
                                            className="avInput avInput--textarea"
                                            placeholder="Indiquez ici les détails de ce produit pour le service RH..."
                                            value={formData.text}
                                            onChange={e => set("text", e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* BLOCK 2: Nature du produit */}
                            <div className="avFormBlock">
                                <div className="avFormBlock__title">2. Nature du Financement</div>
                                <div className="avWizGrid">
                                    <div className="avWizField is-full">
                                        <label>Nature du produit <span className="avReq">*</span></label>
                                        <div className="avMethodCards">
                                            {[
                                                { val: 'avance', title: 'Avance', desc: 'Remboursement intégral en une seule échéance.' },
                                                { val: 'pret', title: 'Prêt', desc: 'Remboursement échelonné sur plusieurs mensualités.' }
                                            ].map(m => (
                                                <button
                                                    key={m.val}
                                                    type="button"
                                                    className={`avMethodCard ${formData.nature === m.val ? 'is-active' : ''}`}
                                                    onClick={() => set("nature", m.val)}
                                                >
                                                    <span className="avMethodCard__icon">{m.icon}</span>
                                                    <span className="avMethodCard__title">{m.title}</span>
                                                    <span className="avMethodCard__desc">{m.desc}</span>
                                                    <span className="avMethodCard__check"><Check size={12} /></span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* BLOCK 3: Remboursement */}
                            <div className="avFormBlock">
                                <div className="avFormBlock__title">3. Modalités & Remboursement</div>
                                <div className="avWizGrid">
                                    {/* Remboursement échelonné (Prêt uniquement) */}
                                    {formData.nature === 'pret' && (
                                        <>
                                            <div className="avWizField">
                                                <label>Minimum du nombre de mensualités</label>
                                                <div className="avNumInput">
                                                    <button type="button" onClick={() => set("mensualitesMin", Math.max(1, (Number(formData.mensualitesMin) || 1) - 1))}><Minus size={14} /></button>
                                                    <input
                                                        type="number"
                                                        className="avInput avInput--center"
                                                        value={formData.mensualitesMin}
                                                        onChange={e => set("mensualitesMin", e.target.value ? Number(e.target.value) : '')}
                                                        min={1}
                                                    />
                                                    <button type="button" onClick={() => set("mensualitesMin", (Number(formData.mensualitesMin) || 0) + 1)}><Plus size={14} /></button>
                                                </div>
                                            </div>

                                            <div className="avWizField">
                                                <label>Maximum du nombre de mensualités <span className="avReq">*</span></label>
                                                <div className="avNumInput">
                                                    <button type="button" onClick={() => set("mensualitesMax", Math.max(1, (Number(formData.mensualitesMax) || 1) - 1))}><Minus size={14} /></button>
                                                    <input
                                                        type="number"
                                                        className={`avInput avInput--center ${stepErrors.mensualitesMax ? 'is-error' : ''}`}
                                                        placeholder="ex: 12"
                                                        value={formData.mensualitesMax}
                                                        onChange={e => set("mensualitesMax", e.target.value ? Number(e.target.value) : '')}
                                                        min={1}
                                                    />
                                                    <button type="button" onClick={() => set("mensualitesMax", (Number(formData.mensualitesMax) || 0) + 1)}><Plus size={14} /></button>
                                                </div>
                                                {stepErrors.mensualitesMax && <span className="avErrMsg">{stepErrors.mensualitesMax}</span>}
                                            </div>

                                            <div className="avWizField">
                                                <label>Montant min mensualité (%)</label>
                                                <input
                                                    type="number"
                                                    className="avInput avInput--center"
                                                    placeholder="ex: 10"
                                                    value={formData.montantMensualiteMinPercent}
                                                    onChange={e => set("montantMensualiteMinPercent", e.target.value ? Number(e.target.value) : '')}
                                                    min={0}
                                                    max={100}
                                                />
                                            </div>

                                            <div className="avWizField">
                                                <label>Montant max mensualité (%)</label>
                                                <input
                                                    type="number"
                                                    className="avInput avInput--center"
                                                    placeholder="ex: 50"
                                                    value={formData.montantMensualiteMaxPercent}
                                                    onChange={e => set("montantMensualiteMaxPercent", e.target.value ? Number(e.target.value) : '')}
                                                    min={0}
                                                    max={100}
                                                />
                                            </div>
                                        </>
                                    )}

                                    {/* Taux & Début */}
                                    <div className="avWizField">
                                        <label>Taux d'intérêt (%)</label>
                                        <input
                                            type="number"
                                            className="avInput"
                                            step="0.01"
                                            placeholder="ex: 2.50"
                                            value={formData.taux}
                                            onChange={e => set("taux", e.target.value)}
                                            min={0}
                                            max={100}
                                        />
                                    </div>

                                    <div className="avWizField">
                                        <label>{formData.nature === 'avance' ? 'Salaire de prélèvement' : 'Échéances autorisées'}</label>
                                        <div className="avContractPills">
                                            {ECHEANCE_OPTIONS.map(option => {
                                                const selected = normalizeEcheances(formData.dateDebut).includes(option.value);
                                                return (
                                                    <button
                                                        key={option.value}
                                                        type="button"
                                                        className={`avContractPill ${selected ? 'is-active' : ''}`}
                                                        onClick={() => toggleEcheance(option.value)}
                                                    >
                                                        {selected && <Check size={13} />}
                                                        {option.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <label style={{ marginTop: '12px' }}>Choix affiché par défaut dans Mes avances</label>
                                        <div className="avSelectWrap">
                                            <select
                                                className="avInput"
                                                value={formData.echeanceParDefaut}
                                                onChange={e => set("echeanceParDefaut", e.target.value)}
                                            >
                                                {normalizeEcheances(formData.dateDebut).map(echeance => (
                                                    <option key={echeance} value={echeance}>{getEcheanceLabel(echeance)}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    {/* Comportement en cas de départ */}
                                    {/*<div className="avWizField is-full" style={{ marginTop: '5px' }}>*/}
                                    {/*    <div className="avInfoBox">*/}
                                    {/*        <Toggle*/}
                                    {/*            checked={!!formData.comportementDepart}*/}
                                    {/*            onChange={v => set("comportementDepart", v)}*/}
                                    {/*        />*/}
                                    {/*        <div>*/}
                                    {/*            <div style={{ fontWeight: 700, fontSize: '12.5px' }}>Remboursement anticipé obligatoire au départ</div>*/}
                                    {/*            <div style={{ fontSize: '11.5px', color: '#64748b' }}>Exiger le solde complet en cas de rupture de contrat.</div>*/}
                                    {/*        </div>*/}
                                    {/*    </div>*/}
                                    {/*</div>*/}
                                </div>
                            </div>

                            {/* BLOCK 5: Éligibilité & Restrictions */}
                            <div className="avFormBlock">
                                <div className="avFormBlock__title">5. Éligibilité & Restrictions</div>
                                <div className="avWizGrid">
                                    <div className="avWizField is-full">
                                        <label>Ancienneté requise (mois)</label>
                                        <input
                                            type="number"
                                            className="avInput"
                                            placeholder="ex: 6"
                                            value={formData.anciennete}
                                            onChange={e => set("anciennete", e.target.value)}
                                            min={0}
                                        />
                                    </div>

                                    <div className="avWizField">
                                        <label>Statuts contractuels éligibles</label>
                                        <div className="avContractPills">
                                            {CONTRACT_OPTIONS.map(opt => {
                                                const isActive = (formData.statutsEligibles || []).includes(opt);
                                                return (
                                                    <button
                                                        key={opt}
                                                        type="button"
                                                        className={`avContractPill ${isActive ? 'is-active' : ''}`}
                                                        onClick={() => toggleContract(opt)}
                                                    >
                                                        {isActive ? `${opt}` : opt}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div className="avWizField">
                                        <label>Limite de demandes <Tip text="Nombre maximum de demandes autorisées par collaborateur pour ce type. Les demandes rejetées ne sont pas comptées." /></label>
                                        <input
                                            type="number"
                                            className="avInput"
                                            placeholder="ex: 4"
                                            value={formData.maxDemandes}
                                            onChange={e => set("maxDemandes", e.target.value)}
                                            min={1}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* BLOCK 6: Justificatifs */}
                            <div className="avFormBlock">
                                <div className="avFormBlock__title">6. Justificatifs & Preuves</div>
                                <div className="avWizGrid">
                                    <div className="avWizField is-full">
                                        <div className="avInfoBox" style={{ background: formData.justificatifRequis ? 'rgba(32, 67, 133, 0.05)' : '#f8fafc' }}>
                                            <Toggle
                                                checked={!!formData.justificatifRequis}
                                                onChange={v => setFormData(p => ({ ...p, justificatifRequis: v, justificatifObligatoire: v }))}
                                            />
                                            <div>
                                                <div style={{ fontWeight: 700, fontSize: '12.5px' }}>Justificatif de besoin requis</div>
                                                <div style={{ fontSize: '11.5px', color: '#64748b' }}>Exiger que le collaborateur joigne une preuve de besoin (ex: Facture, Devis).</div>
                                            </div>
                                        </div>
                                    </div>


                                    {/*<div className="avWizField is-full">*/}
                                    {/*    <div className="avInfoBox" style={{ background: formData.ribObligatoire ? 'rgba(32, 67, 133, 0.05)' : '#f8fafc' }}>*/}
                                    {/*        <Toggle*/}
                                    {/*            checked={!!formData.ribObligatoire}*/}
                                    {/*            onChange={v => set("ribObligatoire", v)}*/}
                                    {/*        />*/}
                                    {/*        <div>*/}
                                    {/*            <div style={{ fontWeight: 700, fontSize: '12.5px' }}>RIB obligatoire avant versement</div>*/}
                                    {/*            <div style={{ fontSize: '11.5px', color: '#64748b' }}>Exiger que le RIB de l'utilisateur soit configuré et validé avant de pouvoir soumettre la demande.</div>*/}
                                    {/*        </div>*/}
                                    {/*    </div>*/}
                                    {/*</div>*/}
                                </div>
                            </div>

                            {/* BLOCK 7: Intégration Paie */}
                            {/*<div className="avFormBlock">*/}
                            {/*    <div className="avFormBlock__title">7. Intégration Paie</div>*/}
                            {/*    <div className="avWizGrid">*/}
                            {/*        <div className="avWizField">*/}
                            {/*            <label>Rubrique d'octroi <Tip text="Code de la rubrique de paie pour le versement (ex: OCT_AV)." /></label>*/}
                            {/*            <input*/}
                            {/*                type="text"*/}
                            {/*                className="avInput"*/}
                            {/*                placeholder="ex: OCT_AV"*/}
                            {/*                value={formData.rubriqueOctroi}*/}
                            {/*                onChange={e => set("rubriqueOctroi", e.target.value.toUpperCase())}*/}
                            {/*            />*/}
                            {/*        </div>*/}

                            {/*        <div className="avWizField">*/}
                            {/*            <label>Rubrique de retenue <Tip text="Code de la rubrique de paie pour le remboursement/retenue (ex: RET_AV)." /></label>*/}
                            {/*            <input*/}
                            {/*                type="text"*/}
                            {/*                className="avInput"*/}
                            {/*                placeholder="ex: RET_AV"*/}
                            {/*                value={formData.rubriqueRetenue}*/}
                            {/*                onChange={e => set("rubriqueRetenue", e.target.value.toUpperCase())}*/}
                            {/*            />*/}
                            {/*        </div>*/}

                            {/*        <div className="avWizField">*/}
                            {/*            <label>Priorité de retenue <Tip text="Ordre de priorité de retenue sur le bulletin de paie (1 étant la plus élevée)." /></label>*/}
                            {/*            <div className="avNumInput">*/}
                            {/*                <button type="button" onClick={() => set("ordrePriorite", Math.max(1, (Number(formData.ordrePriorite) || 1) - 1))}><Minus size={14} /></button>*/}
                            {/*                <input*/}
                            {/*                    type="number"*/}
                            {/*                    className="avInput avInput--center"*/}
                            {/*                    value={formData.ordrePriorite}*/}
                            {/*                    onChange={e => set("ordrePriorite", e.target.value ? Number(e.target.value) : '')}*/}
                            {/*                    min={1}*/}
                            {/*                />*/}
                            {/*                <button type="button" onClick={() => set("ordrePriorite", (Number(formData.ordrePriorite) || 0) + 1)}><Plus size={14} /></button>*/}
                            {/*            </div>*/}
                            {/*        </div>*/}
                            {/*    </div>*/}
                            {/*</div>*/}


                        </form>
                    </div>

                    {/* Footer */}
                    <div className="avModal__footer">
                        <button className="avBtn avBtn--ghost" onClick={closeModal} type="button" disabled={loading}>
                            Annuler
                        </button>
                        <button className="avBtn avBtn--primary" onClick={handleSubmit} type="button" disabled={loading}>
                            <Check size={16} /> {isEdit ? 'Enregistrer les modifications' : 'Créer le produit'}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderViewModal = () => {
        if (!viewItem) return null;
        const v = viewItem;
        const extra = parseDescription(v.description);
        const isPret = v.categorie === 'pret';

        const hasValue = (val) => val !== null && val !== undefined && val !== '';
        const display = (val, fallback = 'Non renseigné') => hasValue(val) ? val : fallback;
        const formatNumber = (val) => hasValue(val) ? Number(val).toLocaleString('fr-FR') : 'Non renseigné';
        const formatPercent = (val) => hasValue(val) ? `${Number(val).toFixed(2)} %` : 'Non renseigné';
        const yesNo = (val) => val ? 'Oui' : 'Non';
        const formatContracts = () => {
            const values = v.typeContrat
                ? v.typeContrat.split(',').map(i => i.trim()).filter(Boolean)
                : extra.statutsEligibles;
            return values?.length ? values.join(', ') : 'Tous';
        };
        const formatJustificatif = () => {
            if (!v.typeJustificatif || v.typeJustificatif === 'AUCUN') return 'Aucun';
            return String(v.typeJustificatif).replaceAll('_', ' ');
        };

        const Section = ({ icon: Icon, title, children }) => (
            <div className="pvSection">
                <div className="pvSection__header">
                    <Icon size={13} style={{ color: extra.couleur }} />
                    <span>{title}</span>
                </div>
                <div className="pvSection__body">{children}</div>
            </div>
        );

        const Row = ({ label, value }) => (
            <div className="pvRow">
                <span className="pvRow__key">{label}</span>
                <span className="pvRow__val">{value ?? 'Non renseigné'}</span>
            </div>
        );

        return (
            <div className="avModal" onClick={e => e.target === e.currentTarget && setViewItem(null)}>
                <div className="avModal__panel avModal__panel--view" role="dialog" aria-modal="true">

                    {/* Header â€” grid row 1 */}
                    <div className="pvHeader">
                        <div className="pvHeader__left">
                            <div className="pvHeader__dot" style={{ background: extra.couleur }} />
                            <div>
                                <div className="pvHeader__title">{v.type}</div>
                                <div className="pvHeader__sub">Détails du produit de financement</div>
                            </div>
                            <span className="pvCode" style={{ background: extra.couleur }}>
                            {extra.code || 'PROD'}
                        </span>
                            <span className={`pvPill ${v.actif ? 'is-on' : 'is-off'}`}>
                            {v.actif ? 'Actif' : 'Inactif'}
                        </span>
                            <span className={`pvPill ${v.categorie === 'avance' ? 'is-avance' : 'is-pret'}`}>
                            {v.categorie === 'avance' ? 'Avance' : 'Prêt'}
                        </span>
                        </div>
                        <button className="pvCloseBtn" type="button" onClick={() => setViewItem(null)}>
                            <X size={16} />
                        </button>
                    </div>

                    {/* Body â€” grid row 1fr (scroll ici) */}
                    <div className="pvBody">
                        {extra.text && (
                            <div className="pvDesc">"{extra.text}"</div>
                        )}

                        <Section icon={Coins} title="Modalités de Financement">
                            {isPret ? (
                                <>
                                    <Row label="Mensualités"
                                         value={`De ${display(extra.mensualitesMin, 1)} à ${display(extra.mensualitesMax, v.nbMensualiteRemboursement || '-')} mois`} />
                                    <Row label="Mensualité min." value={formatPercent(v.montantMensualiteMinPercent)} />
                                    <Row label="Mensualité max." value={formatPercent(v.montantMensualiteMaxPercent)} />
                                </>
                            ) : (
                                <Row label="Salaire de prélèvement" value={formatEcheances(extra.dateDebut)} />
                            )}
                            <Row label="Taux d'intérêt"        value={formatPercent(v.tauxInteret || 0)} />
                            <Row label="Mode de retenue"        value={v.modeRetenue === 'autre' ? display(v.autreModeRetenue) : display(v.modeRetenue, 'Salaire')} />
                            <Row label="Démarrage prélèvement"  value={formatEcheances(extra.dateDebut)} />
                        </Section>

                        <Section icon={Shield} title="Suivi">
                            <Row label="Délai de réponse max"   value={`${display(extra.delaiReponse, 3)} jour(s)`} />
                            <Row label="Notifications"          value={yesNo(extra.notifications)} />
                            <Row label="Statut"
                                 value={v.actif ? 'Actif' : 'Inactif'} />
                        </Section>

                        <Section icon={Users} title="Éligibilité & Restrictions">
                            <Row label="Ancienneté minimale"
                                 value={v.ancienneteMinimale > 0 ? `${v.ancienneteMinimale} mois` : 'Aucune'} />
                            <Row label="Contrats autorisés"     value={formatContracts()} />
                            <Row label="Demandes max"           value={display(extra.maxDemandes, 1)} />
                            <Row label="Délai de carence"
                                 value={extra.delaiCarence > 0 ? `${extra.delaiCarence} mois` : 'Aucun'} />
                        </Section>

                        <Section icon={FileText} title="Justificatifs requis">
                            <Row label="Type de justificatif"     value={formatJustificatif()} />
                            <Row label="Obligatoire à soumission" value={yesNo(v.obligatoire)} />
                            <Row label="RIB requis"               value={yesNo(extra.ribObligatoire)} />
                        </Section>

                        <Section icon={Layers} title="Intégration Paie">
                            <Row label="Rubrique d'octroi"   value={extra.rubriqueOctroi  || 'Non définie'} />
                            <Row label="Rubrique de retenue" value={extra.rubriqueRetenue || 'Non définie'} />
                            <Row label="Ordre de priorité"   value={`${extra.ordrePriorite || 1}`} />
                        </Section>
                    </div>

                    {/* Footer â€” grid row auto */}
                    <div className="avModal__footer">
                        <button className="avBtn avBtn--ghost" type="button" onClick={() => setViewItem(null)}>
                            Fermer
                        </button>
                        <button className="avBtn avBtn--primary" type="button"
                                onClick={() => { setViewItem(null); openEdit(v); }}>
                            <Edit size={14} /> Modifier
                        </button>
                    </div>

                </div>
            </div>
        );
    };
    /* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
       MAIN RENDER
    â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
    return (
        <div className="avPage">
            {/* Header */}
            <div className="avHeader">
                <div>
                    <h1>Gestion des avances & prêts</h1>
                    <p>Configurez les types d'avances et prêts, conditions d'éligibilité et modalités de remboursement.</p>
                    {error && <div style={{ marginTop: 8, color: '#ef4444', fontWeight: 700 }}>Erreur : {error}</div>}
                </div>
            </div>

            <div className="avWrap">
                <div className="avCard">
                    <div className="avBody">
                        {/* â”€â”€ TAB 1: CATALOGUE â”€â”€ */}
                        <div className="avTopRow">
                                    <h2 className="avTitle">Types d'avances et prêts configurés</h2>
                                    <button
                                        className="avBtn avBtn--primary"
                                        type="button"
                                        onClick={openCreate}
                                        disabled={loading}
                                    >
                                        <Plus size={16} /> Ajouter un nouveau produit
                                    </button>
                                </div>

                                <div className="avTableWrap">
                                    <table className="avTable">
                                        <thead>
                                        <tr>
                                            <th>Type</th><th>Catégorie</th><th>Taux</th><th>Remboursement</th><th>Statut</th><th className="is-right">Actions</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {loading ? <tr><td colSpan={6} className="avEmptyCell">Chargement du catalogue...</td></tr>
                                            : (avances || []).length === 0 ? <tr><td colSpan={6} className="avEmptyCell">Aucun produit de financement n'est configuré pour le moment.</td></tr>
                                            : (avances || []).map((avance) => {
                                                const extra = parseDescription(avance.description);
                                                const isAvance = avance.categorie === 'avance';
                                                return <tr key={avance.id}>
                                                    <td><div className="avTableStrong">{avance.type}</div><div className="avTableMuted">{extra.code || 'AV'}</div></td>
                                                    <td><span className={`avCardBadge ${isAvance ? 'is-avance' : 'is-pret'}`}>{isAvance ? 'Avance' : 'Prêt'}</span></td>
                                                    <td>{(avance.tauxInteret || 0).toFixed(2)} %</td>
                                                    <td>{isAvance ? '1 échéance' : `${avance.nbMensualiteRemboursement || extra.mensualitesMax || 1} mensualités`}</td>
                                                    <td><button className={`avStatusPill ${avance.actif ? 'is-active' : 'is-inactive'}`} onClick={() => toggleActif(avance)} disabled={loading} type="button">{avance.actif ? 'Actif' : 'Inactif'}</button></td>
                                                    <td className="is-right"><div className="avTableActions">
                                                        <button className="avCardActionBtn is-view" onClick={() => setViewItem(avance)} title="Détails" type="button"><Eye size={15} /></button>
                                                        <button className="avCardActionBtn is-edit" onClick={() => openEdit(avance)} title="Modifier" type="button" disabled={loading}><Edit size={15} /></button>
                                                        <button className="avCardActionBtn is-duplicate" onClick={() => handleDuplicate(avance)} title="Dupliquer" type="button" disabled={loading}><Copy size={15} /></button>
                                                        <button className="avCardActionBtn is-danger" onClick={() => handleDelete(avance)} title="Supprimer" type="button" disabled={loading}><Trash2 size={15} /></button>
                                                    </div></td>
                                                </tr>;
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="avCardsGrid" aria-hidden="true">
                                    {loading ? (
                                        <div className="avEmptyCell" style={{ gridColumn: '1 / -1' }}>
                                            Chargement du catalogue...
                                        </div>
                                    ) : (avances || []).length === 0 ? (
                                        <div className="avEmptyCell" style={{ gridColumn: '1 / -1' }}>
                                            Aucun produit de financement n'est configuré pour le moment.
                                        </div>
                                    ) : (
                                        (avances || []).map((avance) => {
                                            const extra = parseDescription(avance.description);
                                            const isAvance = avance.categorie === 'avance';

                                            return (
                                                <div key={avance.id} className="avProductCard">
                                                    <div>
                                                        {/* Header: Badges */}
                                                        <div className="avCardHeader">
                                                            <div className="avCardHeaderLeft">
                                                                <span className={`avCardBadge ${isAvance ? 'is-avance' : 'is-pret'}`}>
                                                                    {isAvance ? 'Avance' : 'Prêt'}
                                                                </span>
                                                                <h3 className="avCardTitle">{avance.type}</h3>
                                                            </div>
                                                            <span className="avCardCode" style={{ background: extra.couleur }}>
                                                                {extra.code || 'AV'}
                                                            </span>
                                                        </div>

                                                        {/* Description */}
                                                        <p className="avCardDescription" style={{ marginTop: '8px' }}>
                                                            {extra.text || "Aucune description fournie pour ce produit de financement."}
                                                        </p>
                                                    </div>

                                                    <div>
                                                        {/* Metrics Grid */}
                                                        <div className="avCardGrid">
                                                            <div className="avCardMetric">
                                                                <span className="avMetricLabel">Taux</span>
                                                                <span className="avMetricValue">
                                                                    <Percent size={13} style={{ color: 'var(--admin-accent)' }} />
                                                                    {(avance.tauxInteret || 0).toFixed(2)} %
                                                                </span>
                                                            </div>
                                                            <div className="avCardMetric">
                                                                <span className="avMetricLabel">Remboursement</span>
                                                                <span className="avMetricValue">
                                                                    {isAvance
                                                                        ? '1 échéance'
                                                                        : `${avance.nbMensualiteRemboursement || extra.mensualitesMax || 1} mensualités`}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        {/* Status direct toggle & active row */}
                                                        <div className="avCardMetaRow">
                                                            <button
                                                                className={`avStatusPill ${avance.actif ? 'is-active' : 'is-inactive'}`}
                                                                onClick={() => toggleActif(avance)}
                                                                disabled={loading}
                                                                type="button"
                                                                title={avance.actif ? 'Désactiver le produit' : 'Activer le produit'}
                                                            >
                                                                <span style={{
                                                                    width: '6px',
                                                                    height: '6px',
                                                                    borderRadius: '50%',
                                                                    background: avance.actif ? '#059669' : '#475569'
                                                                }} />
                                                                {avance.actif ? 'Actif' : 'Inactif'}
                                                            </button>

                                                            <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600 }}>
                                                                Anc. requise : {avance.ancienneteMinimale > 0 ? `${avance.ancienneteMinimale} m` : 'Aucune'}
                                                            </span>
                                                        </div>

                                                        {/* Actions Panel */}
                                                        <div className="avCardActions">
                                                            <button
                                                                className="avCardActionBtn"
                                                                onClick={() => setViewItem(avance)}
                                                                title="Détails complets"
                                                                type="button"
                                                            >
                                                                <Eye size={15} />
                                                            </button>
                                                            <button
                                                                className="avCardActionBtn"
                                                                onClick={() => openEdit(avance)}
                                                                title="Modifier"
                                                                type="button"
                                                                disabled={loading}
                                                            >
                                                                <Edit size={15} />
                                                            </button>
                                                            <button
                                                                className="avCardActionBtn"
                                                                onClick={() => handleDuplicate(avance)}
                                                                title="Dupliquer le produit"
                                                                type="button"
                                                                disabled={loading}
                                                            >
                                                                <Copy size={15} />
                                                            </button>
                                                            <button
                                                                className="avCardActionBtn is-danger"
                                                                onClick={() => handleDelete(avance)}
                                                                title="Supprimer"
                                                                type="button"
                                                                disabled={loading}
                                                            >
                                                                <Trash2 size={15} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>

                    </div>
                </div>
            </div>

            {/* Render drawers & modals */}
            {renderModalForm()}
            {renderViewModal()}
        </div>
    );
};

export default ParamAvances;
